import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    Button
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Layout } from 'components/atoms'
import { InputBuild, MultiSelectBuild, SelectBuild } from 'components/molecules'
import { useCacheFilters } from 'hooks/useCacheFilters'
import { forwardRef, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { initialState } from './constants'
import { formFilter } from './form'
import { FilterManagementReport } from './interfaces'
import { schema } from './schema'
import { FormFilterElements } from './types'

interface FilterProps {
    handleFilter: (value: FilterManagementReport) => void
    loadingFilter?: boolean
}

export interface FilterRef {
    setValues: (value: FilterManagementReport) => void
}

const Filter = forwardRef<FilterRef, FilterProps>(
    ({ handleFilter, loadingFilter }, ref) => {
        const formData = useMemo(() => formFilter, [formFilter])

        const OPEN_TAB = [0]
        const CLOSE_TAB: any = []

        const { hasCacheFilters, resetCacheFilters, getCacheFilters } =
            useCacheFilters()

        const defaultIndex = hasCacheFilters ? OPEN_TAB : CLOSE_TAB

        const setCacheFilters = () => {
            const cacheFilters = getCacheFilters()

            if (!cacheFilters) return

            cacheFilters?.filter &&
                Object.entries(cacheFilters.filter).forEach(([key, value]) => {
                    setValue(
                        `${key as keyof FilterManagementReport}`,
                        value as any
                    )
                })
        }

        const {
            register,
            formState: { errors },
            watch,
            handleSubmit,
            setValue,
            reset,
            control
        } = useForm<FilterManagementReport>({
            resolver: yupResolver(schema),
            mode: 'onChange'
        })

        const handleClear = () => {
            resetCacheFilters()
            reset({ ...initialState })
            handleFilter({} as any)
        }

        const onSubmit = (value: FilterManagementReport) => {
            resetCacheFilters()
            handleFilter(value)
        }

        useEffect(() => {
            setCacheFilters()
        }, [])

        return (
            <Accordion
                allowMultiple
                backgroundColor={'white'}
                marginBottom={4}
                defaultIndex={defaultIndex}
            >
                <AccordionItem>
                    <h2>
                        <AccordionButton
                            backgroundColor={'white'}
                            className="white-hover"
                        >
                            <Box
                                as="h2"
                                flex="1"
                                textAlign="left"
                                fontSize={20}
                                fontWeight={700}
                            >
                                Filtrar Relatório(s):

                                <Box
                                    as="span"
                                    marginLeft={3}
                                    fontSize="sm"
                                    fontWeight="normal"
                                    color="gray.500"
                                >
                                    Sem filtros informados, são exibidos apenas
                                    os registros dos últimos 3 meses.
                                </Box>
                            </Box>
                            <AccordionIcon />
                        </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4} backgroundColor={'white'}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Box backgroundColor={'white'}>
                                <Layout type="row" gutter={{ y: 1.5 }}>
                                    {formData.map(
                                        (
                                            {
                                                layout,
                                                input,
                                                select,
                                                multiSelect
                                            },
                                            key
                                        ) => (
                                            <Layout {...layout} key={key}>
                                                {multiSelect ? (
                                                    <Controller
                                                        name={
                                                            multiSelect.id as keyof FilterManagementReport
                                                        }
                                                        control={control}
                                                        defaultValue=""
                                                        render={({ field }) => (
                                                            <MultiSelectBuild
                                                                {...multiSelect}
                                                                value={
                                                                    field.value ||
                                                                    ''
                                                                }
                                                                onChange={
                                                                    field.onChange
                                                                }
                                                                onBlur={
                                                                    field.onBlur
                                                                }
                                                                err={
                                                                    errors[
                                                                        multiSelect.id as keyof FilterManagementReport
                                                                    ] as any
                                                                }
                                                            />
                                                        )}
                                                    />
                                                ) : select ? (
                                                    <SelectBuild
                                                        {...select}
                                                        err={
                                                            errors[
                                                                select.id as FormFilterElements
                                                            ]
                                                        }
                                                        {...register(
                                                            select.id as FormFilterElements
                                                        )}
                                                    />
                                                ) : (
                                                    input && (
                                                        <InputBuild
                                                            {...input}
                                                            err={
                                                                errors[
                                                                    input.id as FormFilterElements
                                                                ]
                                                            }
                                                            {...register(
                                                                input.id as FormFilterElements
                                                            )}
                                                            value={watch(
                                                                input?.id as FormFilterElements
                                                            )}
                                                        />
                                                    )
                                                )}
                                            </Layout>
                                        )
                                    )}
                                </Layout>
                                <Box
                                    paddingY={8}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    gap={6}
                                >
                                    <Button
                                        colorScheme="primary"
                                        variant="outline"
                                        onClick={handleClear}
                                    >
                                        Limpar Campos
                                    </Button>
                                    <Button
                                        colorScheme="primary"
                                        type="submit"
                                        isLoading={loadingFilter}
                                    >
                                        Filtrar
                                    </Button>
                                </Box>
                            </Box>
                        </form>
                    </AccordionPanel>
                </AccordionItem>
            </Accordion>
        )
    }
)

export { Filter }
